import { motion } from "framer-motion";
import { DollarSign, CalendarDays, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";

interface Revenue {
  id: string;
  amount: number;
  fee_type: string;
  created_at: string;
  event_id: string;
}

interface AdminRevenueProps {
  filteredRevenue: Revenue[];
  filteredTotalRevenue: number;
  filteredRevenueByType: Record<string, number>;
  dateFilter: "today" | "week" | "month" | "all" | "custom";
  setDateFilter: (filter: "today" | "week" | "month" | "all" | "custom") => void;
  customDateRange: { from: Date | undefined; to: Date | undefined };
  setCustomDateRange: (range: { from: Date | undefined; to: Date | undefined }) => void;
}

const AdminRevenue = ({
  filteredRevenue,
  filteredTotalRevenue,
  filteredRevenueByType,
  dateFilter,
  setDateFilter,
  customDateRange,
  setCustomDateRange,
}: AdminRevenueProps) => {
  const typeLabels: Record<string, { label: string; color: string; icon: string; isFiat: boolean }> = {
    event_creation: { label: "Event Creation", color: "text-cyan-500", icon: "🎯", isFiat: false },
    platform_fee: { label: "Platform Fees", color: "text-primary", icon: "💰", isFiat: false },
    "platform_fee_5%": { label: "Platform Fees (5%)", color: "text-primary", icon: "💰", isFiat: false },
    "full_fee_7%": { label: "Full Fees (7%)", color: "text-primary", icon: "💰", isFiat: false },
    creator_fee: { label: "Creator Fees", color: "text-success", icon: "👤", isFiat: false },
    coin_purchase: { label: "Purchased Coin Revenue", color: "text-amber-500", icon: "🪙", isFiat: true },
    subscription: { label: "Subscriptions", color: "text-purple-500", icon: "⭐", isFiat: true },
    subscription_renewal: { label: "Renewals", color: "text-pink-500", icon: "🔄", isFiat: true },
  };

  const typeColors: Record<string, string> = {
    event_creation: "bg-cyan-500/20 text-cyan-500",
    platform_fee: "bg-primary/20 text-primary",
    "platform_fee_5%": "bg-primary/20 text-primary",
    "full_fee_7%": "bg-primary/20 text-primary",
    creator_fee: "bg-success/20 text-success",
    coin_purchase: "bg-amber-500/20 text-amber-500",
    subscription: "bg-purple-500/20 text-purple-500",
    subscription_renewal: "bg-pink-500/20 text-pink-500",
  };

  return (
    <div className="space-y-6">
      {/* Date Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-4"
      >
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <CalendarDays size={16} />
            Filter by:
          </span>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={dateFilter === "today" ? "default" : "outline"}
              size="sm"
              onClick={() => setDateFilter("today")}
            >
              Today
            </Button>
            <Button
              variant={dateFilter === "week" ? "default" : "outline"}
              size="sm"
              onClick={() => setDateFilter("week")}
            >
              Last 7 Days
            </Button>
            <Button
              variant={dateFilter === "month" ? "default" : "outline"}
              size="sm"
              onClick={() => setDateFilter("month")}
            >
              Last 30 Days
            </Button>
            <Button
              variant={dateFilter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setDateFilter("all")}
            >
              All Time
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={dateFilter === "custom" ? "default" : "outline"}
                  size="sm"
                >
                  <Calendar size={14} className="mr-1" />
                  Custom
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="range"
                  selected={{
                    from: customDateRange.from,
                    to: customDateRange.to,
                  }}
                  onSelect={(range) => {
                    setCustomDateRange({ from: range?.from, to: range?.to });
                    if (range?.from) setDateFilter("custom");
                  }}
                  numberOfMonths={2}
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
          {dateFilter === "custom" && customDateRange.from && (
            <span className="text-xs text-muted-foreground">
              {format(customDateRange.from, "MMM d, yyyy")} 
              {customDateRange.to && ` - ${format(customDateRange.to, "MMM d, yyyy")}`}
            </span>
          )}
        </div>
      </motion.div>

      {/* Revenue Breakdown by Type */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="glass-card p-6"
      >
        <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
          <DollarSign size={20} className="text-primary" />
          Revenue Breakdown
          <span className="text-sm font-normal text-muted-foreground ml-2">
            ({filteredRevenue.length} transactions)
          </span>
        </h2>
        
        {Object.keys(filteredRevenueByType).length === 0 ? (
          <p className="text-muted-foreground text-center py-4">No revenue recorded for this period</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(filteredRevenueByType).map(([type, amount]) => {
              const typeInfo = typeLabels[type] || { label: type.replace(/_/g, " "), color: "text-muted-foreground", icon: "📊", isFiat: false };
              const displayAmount = typeInfo.isFiat ? `$${amount.toFixed(2)}` : `${amount.toFixed(2)} DC`;
              
              return (
                <div
                  key={type}
                  className="p-4 rounded-lg bg-muted/30 border border-border/50"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{typeInfo.icon}</span>
                    <span className="text-sm font-medium text-muted-foreground">{typeInfo.label}</span>
                  </div>
                  <p className={`text-2xl font-bold ${typeInfo.color}`}>
                    {displayAmount}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {filteredRevenue.filter(r => r.fee_type === type).length} transactions
                  </p>
                </div>
              );
            })}
            
            {/* Total */}
            <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">💎</span>
                <span className="text-sm font-medium text-primary">Total Revenue</span>
              </div>
              <p className="text-2xl font-bold text-primary">
                ${filteredTotalRevenue.toFixed(2)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {filteredRevenue.length} transactions
              </p>
            </div>
          </div>
        )}
      </motion.div>

      {/* Recent Transactions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="glass-card p-6"
      >
        <h2 className="text-xl font-bold text-foreground mb-4">Transactions</h2>
        
        {filteredRevenue.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">No transactions for this period</p>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {filteredRevenue.slice(0, 50).map((r) => {
              const colorClass = typeColors[r.fee_type] || "bg-muted text-muted-foreground";
              
              return (
                <div
                  key={r.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/30"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString()}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${colorClass}`}>
                      {r.fee_type.replace(/_/g, " ")}
                    </span>
                  </div>
                  <span className="font-bold text-success">+${Number(r.amount).toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminRevenue;
